import React, { useState } from 'react'
import {
  CardsContainer,
  LabraToolTipV2,
  OfferCard,
  TableComponentV2,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faCircleExclamation,
  faClock,
} from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './UsageListingView.styles'
import { ShortenedNameV2 } from '@labrav/common'
import { getCloudImg } from '../../../common/utils/getCloudImg'
import { TableCell } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  BuyerDetailRow,
  BuyerInfo,
} from '../../../common/components/DetailRow/DetailRow'
import { UsageData, UsageStatusMap } from '../../../modules/usageMetering/types'
import { formatToDecimalPlaces } from '../../../common/utils/meteringHelpers'
import { standardLabraDateFormat } from '../../../common/utils/meteringConstants'
import { DateTime as DateTimeLuxon } from 'luxon'
import { dateAndTimeConversion } from '../../../common/utils/dateAndTimeConversion'
import { BuyerLogo } from '../../../common/components/BuyerLogo/BuyerLogo'
import { CloudMarketplace } from '../../../modules/productsListing/types'
import { useParams } from 'react-router-dom'
interface UsageListingViewProps {
  usageData: UsageData
}

export const UsageListingView: React.FC<UsageListingViewProps> = ({
  usageData,
}) => {
  const { classes } = useStyles()
  const [showTable, setShowTable] = useState(false)
  const { cloud } = useParams<{ cloud: CloudMarketplace }>()
  const mapping = {
    Dimensions: 'name',
    'Price per bandwidth': 'pricePerBandwidth',
    Quantity: 'quantity',
    Amount: 'price',
  }
  const totalValue =
    (usageData?.meteredQuantity ?? 0) * (usageData?.dimension?.price ?? 0)
  const customHeader = ({
    unit,
    cloudProvider,
  }: {
    unit: string
    cloudProvider?: CloudMarketplace
  }) => {
    const updatedUnit = unit ? transformMetric(unit) : '-'
    return {
      name: (
        <TableCell className={classes.tableCell}>
          <div className={classes.tableHeading}>Dimensions</div>
        </TableCell>
      ),
      pricePerBandwidth: (
        <TableCell className={classes.tableCell}>
          <div className={classes.tableHeading}>
            Price {cloudProvider === 'AWS' ? 'per' : ''} {updatedUnit}
          </div>
        </TableCell>
      ),
      quantity: (
        <TableCell className={classes.tableCell}>
          <div className={classes.tableHeading}>Quantity</div>
        </TableCell>
      ),
      price: (
        <TableCell className={classes.tableCell}>
          <div className={classes.tableHeading}>Amount</div>
        </TableCell>
      ),
    }
  }
  const customObject = [
    {
      id: usageData?.dimension?.labraDimensionId || '-',
      name: usageData?.dimension?.name,
      pricePerBandwidth: (
        <div>
          {usageData?.dimension?.price !== null ? (
            <>
              <span className={classes.dollarCss}>$</span>
              {formatToDecimalPlaces(usageData?.dimension?.price, 2)}
            </>
          ) : (
            '-'
          )}
        </div>
      ),
      quantity: String(usageData?.meteredQuantity) || '-',
      price: (
        <div>
          <span className={classes.dollarCss}>$</span>
          {formatToDecimalPlaces(totalValue, 2)}
        </div>
      ),
    },
  ]
  const formatedMeteringdate = dateAndTimeConversion(
    usageData.meteringSubmissionTimestamp,
    'UTC'
  )
  const getNormalizedCloud = (cloud?: string) => {
    if (!cloud) return 'AWS'

    switch (cloud.toUpperCase()) {
      case 'AZURE':
        return 'MICROSOFT'
      case 'AWS':
        return 'AWS'
      case 'GCP':
        return 'GCP'
      default:
        return 'AWS'
    }
  }

  const isMicrosoft = usageData?.productDetails?.cloud === 'AZURE'

  let logoStatusDescription = ''
  switch (usageData?.labraMeteringStatus) {
    case UsageStatusMap.COMPLETED_WITH_SUCCESS:
      logoStatusDescription = 'Metering successful'
      break
    case UsageStatusMap.ACCEPTED:
      logoStatusDescription = 'Metering successful'
      break
    case UsageStatusMap.FAILED:
    case UsageStatusMap.COMPLETED_WITHOUT_SUCCESS:
    case UsageStatusMap.SUBMISSION_FAILED_BY_AWS:
    case UsageStatusMap.SUBMISSION_FAILED_BY_LABRA:
      logoStatusDescription = 'Metering failed'
      break
    case UsageStatusMap.PENDING:
    case UsageStatusMap.SUBMITTED:
      logoStatusDescription = `Metering in progress since ${formatedMeteringdate.dateString} `
      break
    case UsageStatusMap.DUPLICATE:
      logoStatusDescription = 'Duplicate metering record'
      break
    default:
      logoStatusDescription = ''
      break
  }

  return (
    <div>
      <CardsContainer>
        <OfferCard
          name={
            <div className={classes.titleWrapper}>
              <div className={classes.name}>
                <ShortenedNameV2
                  name={
                    usageData?.buyerDetails?.buyerCompanyName ||
                    usageData?.buyerDetails?.cloudCustomerId
                  }
                  length={40}
                ></ShortenedNameV2>
              </div>
              <div className={classes.logoStatus}>
                <UsageStatus
                  status={usageData?.labraMeteringStatus as any}
                  tooltipDesc={logoStatusDescription}
                />
              </div>
            </div>
          }
          customLogo={
            <BuyerLogo
              logoUrl={usageData?.buyerDetails?.buyerLogoUrl}
              classes={classes}
            />
          }
          isLastCard
          customiseForBuyer
          hideLogo={false}
          rightSideSection={
            <>
              <img
                src={getCloudImg(
                  getNormalizedCloud(usageData?.productDetails?.cloud)
                )}
                data-testid={`cloud-icon-${usageData?.productDetails?.cloud}`}
                style={{ width: '24px', height: '24px' }}
              />
            </>
          }
        />
        <BuyerDetailRow
          children={
            <>
              <BuyerInfo
                title={'Product name'}
                value={
                  <ShortenedNameV2
                    showClipboardTooltip={true}
                    length={20}
                    name={usageData?.productDetails?.productName}
                  />
                }
                gridSize={isMicrosoft ? 3 : 4}
              />
              {usageData?.productDetails?.cloud === 'AZURE' && (
                <BuyerInfo
                  title={'Purchased Plan'}
                  value={
                    <ShortenedNameV2
                      showClipboardTooltip={true}
                      length={20}
                      name={usageData?.productDetails?.purchasePlan}
                    />
                  }
                  gridSize={3}
                />
              )}

              <BuyerInfo
                title={'Metering date'}
                value={
                  <LabraToolTipV2
                    tooltipPlacement="top"
                    description={formatedMeteringdate.dateString}
                    tooltipPlacmentGap="27px"
                  >
                    <div>
                      {DateTimeLuxon.fromISO(
                        usageData?.meteringSubmissionTimestamp
                      ).toFormat(standardLabraDateFormat)}
                    </div>
                  </LabraToolTipV2>
                }
                gridSize={isMicrosoft ? 3 : 4}
              />
              <BuyerInfo
                title={'Amount'}
                value={
                  <>
                    <span className={classes.currency}>$</span>
                    {formatToDecimalPlaces(totalValue, 2)}
                  </>
                }
                gridSize={isMicrosoft ? 2 : 3}
                showDivider={false}
              />
              <BuyerInfo
                value={
                  <div className={classes.accordianWrapper}>
                    <div className={classes.verticalDivider}></div>
                    {!showTable ? (
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        onClick={() => setShowTable(!showTable)}
                        border={true}
                        className={classes.copyIcon}
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faChevronUp}
                        onClick={() => setShowTable(!showTable)}
                        border={true}
                        className={classes.copyIcon}
                      />
                    )}
                  </div>
                }
                gridSize={1}
                showDivider={false}
              />
            </>
          }
        />
        {showTable && (
          <div className={classes.tableWrapper}>
            <TableComponentV2
              mapping={mapping}
              objects={customObject}
              customHeader={customHeader({
                unit: usageData?.dimension?.dimensionUnit,
                cloudProvider: cloud,
              })}
              count={10}
              keyName="dimensional-breakdown-metering-data."
              showCheckbox={false}
              hasMoreData={false}
              fetchMoreData={() => void 0}
              selectedItem={[]}
              onChangeCheckbox={() => void 0}
              isLoading={false}
              isBasicTable={false}
              tableHeight="fit-content"
            />
          </div>
        )}
      </CardsContainer>
    </div>
  )
}
interface UsageStatusProps {
  status: UsageStatusMap
  tooltipDesc?: string
}

type StatusLabel = 'Success' | 'Failed' | 'In-progress'

const getStatusIcons = (
  theme: any
): Record<StatusLabel, { icon: any; color: string }> => ({
  Success: {
    icon: faCircleCheck,
    color: theme.palette.listingStatus.main,
  },
  Failed: {
    icon: faCircleExclamation,
    color: theme.palette.stages.failed,
  },
  'In-progress': {
    icon: faClock,
    color: theme.palette.chips.contrastText,
  },
})

const getStatusLabel = (status: UsageStatusMap): StatusLabel | undefined => {
  switch (status) {
    case UsageStatusMap.PENDING:
    case UsageStatusMap.SUBMITTED:
      return 'In-progress'
    case UsageStatusMap.COMPLETED_WITH_SUCCESS:
      return 'Success'
    case UsageStatusMap.FAILED:
    case UsageStatusMap.COMPLETED_WITHOUT_SUCCESS:
    case UsageStatusMap.SUBMISSION_FAILED_BY_AWS:
    case UsageStatusMap.SUBMISSION_FAILED_BY_LABRA:
      return 'Failed'
    case UsageStatusMap.ACCEPTED:
      return 'Success'
    case UsageStatusMap.DUPLICATE:
      return 'Failed'
    default:
      return undefined
  }
}
const UsageStatus: React.FC<UsageStatusProps> = ({ status, tooltipDesc }) => {
  const theme = useTheme()
  const statusIcons = getStatusIcons(theme)
  const label = getStatusLabel(status)
  const config = label ? statusIcons[label] : undefined

  if (!config) return null
  return (
    <LabraToolTipV2
      tooltipPlacement="top-start"
      description={tooltipDesc}
      tooltipPlacmentGap="2px"
    >
      <FontAwesomeIcon
        icon={config.icon}
        style={{ width: 16, height: 16, color: config.color }}
      />
    </LabraToolTipV2>
  )
}

export default UsageStatus
export { getStatusLabel, UsageStatus }
export const transformMetric = (metric: string) => {
  const [prefix, suffix] = metric.split('-')
  return suffix ? `${prefix.toLowerCase()} (${suffix})` : prefix.toLowerCase()
}
